// @flow

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectRequests } from '../../reduxStore/modules/feed';

import Hidden from '@mui/material/Hidden';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { log } from '../../utils/jsUtils';
import RequestHistoryEntry from './RequestHistoryEntry';
import TaskV2Activity from './TaskV2Activity';
import FeedRow from './FeedRow';
import SendReplyBox from '../Tenants/SendReplyBox';
import { createClasses } from '../../utils/stylesUtils';

import { calculateCreditsForMsg } from '../../models/Message';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { selectAccountUsage } from '../../reduxStore/modules/user/selectors';
import { useI18n } from '../../contexts/I18nContext';

import styles from './Styles/RequestContent.Style';
import listTableStyles from '../Styles/CommonListTable.Style';
import FeedTableStyles from './Styles/CommonFeedTable.Style';
import SPBackend from '../../services/SPBackend';

import type { ChatEntry, Task, Unit, Tenant } from '../../services/SPBackend';

type Props = {
  classes: Object,
  requestHistory: Array<ChatEntry>,
  isV2Format: boolean,
  request: Task,
  tenant: Tenant,
  setTenant: Function,
  unit: Unit,
  loadingRequest: boolean,
  updateTaskCallback: Function,
  refreshRequestDetails: Function,
  unreadCount: number
};

const AlwaysScrollToBottom = (props: {
  setScrollRef: Function,
  refreshScrollDown: boolean,
  onScrollComplete?: Function
}) => {
  const {
    setScrollRef,
    refreshScrollDown,
    onScrollComplete
  } = props;

  log('[AlwaysScrollToBottom]...');
  const elementRef = useRef();
  const scrollToBottom = () => {
    setTimeout(() => {
      if (elementRef.current) {
        elementRef.current.scrollIntoView({ behavior: 'instant' });
        if (onScrollComplete) {
          onScrollComplete();
        }
      }
    }, 50);
  };
  useEffect(() => {
    setScrollRef();
    scrollToBottom();
  }, [setScrollRef]);
  useEffect(() => {
    log('[useEffect] refresh scroll down...');
    setScrollRef();
    scrollToBottom();
  }, [refreshScrollDown, setScrollRef]);
  return <div ref={elementRef} style={{ scrollMarginBottom: 180 }} />;
};

// eslint-disable-next-line react/no-multi-comp
const RequestContent = (props: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = createClasses({
    ...listTableStyles,
    ...styles,
    ...FeedTableStyles
  }, theme);

  const {
    requestHistory,
    isV2Format,
    tenant,
    setTenant,
    unreadCount,
    request,
    unit,
    loadingRequest,
    updateTaskCallback,
    refreshRequestDetails
  } = props;

  const [scrollRef, setScrollRef] = useState(null);
  const [refreshScrollDown, setRefreshScrollDown] = useState(true);
  const [isScrollReady, setIsScrollReady] = useState(false);

  // Use Redux selectors instead of direct store access
  const user = useSelector(selectUser);
  const accountUsage = useSelector(selectAccountUsage);
  const i18n = useI18n();

  // Subscribe to Redux requests for optimistic tag updates
  // TagCustomersPicker dispatches UPDATE_REQUESTS to Redux, so we use Redux version if available
  const reduxRequests = useSelector(selectRequests);

  // Find this request in Redux (may have optimistic tag updates)
  const reduxRequest = useMemo(() => {
    if (!request?._id || !reduxRequests?.length) return null;
    return reduxRequests.find(r => r._id === request._id);
  }, [request?._id, reduxRequests]);

  // Use Redux request if available (has optimistic updates), fallback to prop
  const effectiveRequest = reduxRequest || request;
  
  const handleSendReply = (tenantIds, messagePayload, successCallback, failureCallback) => {
    log('[handleSendReply][sendPMToTenant] tenantIds, message: ', tenantIds, messagePayload);
    log('[handleSendReply][sendPMToTenant] msg line split: ', messagePayload.message.split('\n'));
    // verify that credit balance is sufficient
    const creditsNeeded = calculateCreditsForMsg(messagePayload);
    log('[sendBroadcast] creditsNeeded: ', creditsNeeded);
    const [tenantId] = tenantIds;
    if (accountUsage.TotalCreditsBalance < creditsNeeded) {
      failureCallback('Insufficient credits. Visit <a href="/billing">Billing</a> to recharge your account or upgrade your plan');
    } else {
      if (user !== null) {
        SPBackend.sendPMToTenant(tenantId, request._id, messagePayload, user)
          .then((response) => {
            const { data } = response;
            log('[handleSendReply][sendPMToTenant] tenantId, response: ', tenantId, response);
            dispatch(setSnackMessage({
              message: data.message,
              level: data.success,
              duration: 3000
            }));
            refreshRequestDetails(() => {
              setTimeout(() => {
                setRefreshScrollDown(!refreshScrollDown);
              }, 300);
            });
            successCallback();
            // setTimeout(() => {
            //   console.log('scrolling down...', scrollRef);
            //   if (scrollRef !== null && typeof scrollRef !== 'undefined' ) {
            //     scrollRef.current.scrollIntoView();
            //   }
            // }, 300);
          })
          .catch(error => {
            log('[handleSendReply][sendPMToTenant] error: ', error);
            dispatch(setSnackMessage({
              message: 'An error occured. Please try again.',
              level: 'error',
              duration: 3000
            }));
            failureCallback();  
          });
      }
    }
  };

  log('[RequestContent] props: ', props);
  return (
    <Box 
      sx={classes.tableRequestContent}
    >
      {
        requestHistory && request
        ? <Box>
          {
            request
            ? [
              /** MOBILE VERSION **/
              <Hidden
                key='preview-mobile'
                mdUp
              >
                <FeedRow
                  sx={{
                    mobileFeedItem: classes.requestPreviewCtrMobile
                  }}
                  key={'request'}
                  readonly
                  request={effectiveRequest}
                  setTenant={setTenant}
                  units={unit ? [unit] : null}
                  updateTaskCallback={updateTaskCallback(request)}
                />
              </Hidden>,
              /** DESKTOP VERSION **/
              <Hidden
                key='preview-desktop'
                smDown
              >
                <Box sx={classes.requestPreviewCtr}>
                  <Table sx={classes.table}>
                    <colgroup>
                      <col style={classes.colRequest} />
                      <col style={classes.colCustomerName} />
                      <col style={classes.colType} />
                      <col style={classes.colRequest} />
                      {/* <col style={classes.colStatus} /> */}
                      <col style={classes.colAssignee} />
                      <col style={classes.colDate} />
                      <col style={classes.colAction} />
                    </colgroup>
                    <TableHead sx={classes.feedTableBody}>
                      <TableRow sx={classes.feedTableHeadRow}>
                        <TableCell sx={classes.customerNameCell}>
                          <span style={{display: 'block', width: 30}} />
                          {i18n.t('Customer')}
                        </TableCell>
                        <TableCell sx={classes.tagCell}>{i18n.t('Tag')}</TableCell>
                        <TableCell sx={classes.requestCell}>
                          {i18n.t('Subject')}
                        </TableCell>
                        <TableCell 
                          align="left"
                          sx={classes.assigneeCell}
                        >{i18n.t('Staff')}</TableCell>
                        {/* <TableCell
                          align='left'
                          className={classes.statusCell}
                        >{'Progress'}
                        </TableCell> */}
                        <TableCell
                          align='right'
                          sx={classes.dateCell}
                        >{'Last Updated'}</TableCell>
                        
                        </TableRow>
                    </TableHead>
                    <TableBody sx={classes.feedTableBody}>
                      <FeedRow
                        key={'request'}
                        readonly
                        request={effectiveRequest}
                        setTenant={setTenant}
                        units={unit ? [unit] : null}
                        updateTaskCallback={updateTaskCallback(request)}
                      />
                    </TableBody>
                  </Table>
                </Box>
              </Hidden>
            ]
            : <Grid style={classes.content}
                item
                xs={12}
              >{'Loading...'}</Grid>
          }
          <Box sx={{ ...classes.messagesArea, opacity: isScrollReady ? 1 : 0 }}>
          {
            isV2Format
            ? <TaskV2Activity
                history={requestHistory}
                task={request}
                tenant={tenant}
              />
            : <List sx={classes.listRoot}>
              {
                requestHistory.map((chat, chatIndex) => {
                  return (
                    <RequestHistoryEntry 
                      chat={chat}
                      firstEntry={chatIndex === (requestHistory.length - 1)}
                      key={`entry-${chatIndex}`}
                      summary={chat.Summary || chat.Comment}
                    />
                        
                    );
                })
              }
              </List>
          }
            <AlwaysScrollToBottom
              refreshScrollDown={refreshScrollDown}
              setScrollRef={setScrollRef}
              onScrollComplete={() => setIsScrollReady(true)}
            />
            <Box sx={classes.replyBox}>
              <SendReplyBox  
                  tenant={tenant}
                  triggerAction={handleSendReply}
              />
            </Box>
          </Box>
          {
            unreadCount > 0
            ? <Box sx={classes.unreadAnchorCtr}>
              <Paper
                sx={classes.unreadAnchorPopperContent}
                onClick={refreshRequestDetails}
              >
                {`${unreadCount} unread message${unreadCount > 1 ? 's' : ''}. Click to read.`}
              </Paper>
            </Box>
            : null
          }
        </Box>
        : loadingRequest
          ? <Grid style={classes.content}
              item
              xs={12}
            >{'Loading...'}</Grid>
          : <Box>{'Request not found.'}</Box>
      }
    </Box>
    
  );
};

export default RequestContent;